import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

// Primary CTA stack: official wallet badges, "Preview pass", the
// "Add to another wallet" disclosure, and the trust line. Once the user has
// tapped an add button and returned to the tab, the stack swaps to the
// pass-added confirmation (open-wallet deep link + web-push opt-in).
//
// Which buttons show follows the pre-redesign platform logic exactly (no
// install-funnel regression): iOS → Apple only; Android → Google only;
// desktop/unknown → both, Google first per the target design.

const Spinner: React.FC = () => (
  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const BadgeButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  img: string;
  alt: string;
}> = ({ onClick, disabled, busy, img, alt }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full h-[52px] rounded-xl bg-black flex items-center justify-center hover:opacity-85 transition disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {busy ? <Spinner /> : <img src={img} alt={alt} className="h-11 w-auto max-w-full object-contain" />}
  </button>
);

export interface WebPushState {
  supported: boolean;
  subscribed: boolean;
  busy: boolean;
  error: string | null;
  onEnable: () => void;
}

// variant controls which slice renders, so the mobile layout can put ONLY
// the wallet badges ABOVE the pass card and everything else below it:
//   'full'    — everything (desktop left column)
//   'main'    — badge buttons only (mobile, above the card)
//   'preview' — Preview pass + add-to-another-wallet + trust line
//               (mobile, below the card)
export type CtaVariant = 'full' | 'main' | 'preview';

const WalletCtaPanel: React.FC<{
  isIOS: boolean;
  isAndroid: boolean;
  googleReady: boolean;
  isDownloading: boolean;
  onApple: () => void;
  onGoogle: () => void;
  onSamsung: () => void;
  samsungAvailable: boolean;
  onPreview: () => void;
  passAdded: boolean;
  onResetAdded: () => void;
  webPush: WebPushState;
  variant?: CtaVariant;
}> = ({
  isIOS,
  isAndroid,
  googleReady,
  isDownloading,
  onApple,
  onGoogle,
  onSamsung,
  samsungAvailable,
  onPreview,
  passAdded,
  onResetAdded,
  webPush,
  variant = 'full',
}) => {
  const [otherOpen, setOtherOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      /* clipboard unavailable — nothing sensible to do */
    }
  };

  if (passAdded) {
    // The confirmation replaces the badge stack; the mobile preview slot
    // below the card has nothing to show.
    if (variant === 'preview') return null;
    return (
      <div className="bg-white rounded-2xl border border-[#E4EAE2] p-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-full bg-[#AEDD1F] text-[#06251B] flex items-center justify-center">
            <svg className="w-4.5 h-4.5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <span className="text-lg font-semibold text-gray-900">Your pass is ready</span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Tap below to open your pass in {isIOS ? 'Apple' : 'Google'} Wallet.
        </p>
        {/* Apple Wallet opens via the legacy `shoebox://` scheme (still
            supported by iOS); Google via the public web URL which Android
            routes into the Wallet app. Neither deep-links to the specific
            pass — the user lands on the wallet home with the new pass on top. */}
        <a
          href={isIOS ? 'shoebox://' : 'https://wallet.google.com'}
          className="inline-flex items-center justify-center px-6 h-11 rounded-full bg-[#06251B] text-white text-sm font-semibold hover:bg-[#0A3B2C] transition"
        >
          Open {isIOS ? 'Apple Wallet' : 'Google Wallet'}
        </a>

        {webPush.supported && !webPush.subscribed && (
          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-700 mb-2.5">
              Want a heads-up about offers and updates from this merchant?
            </p>
            <button
              type="button"
              onClick={webPush.onEnable}
              disabled={webPush.busy}
              className="inline-flex items-center justify-center px-5 h-10 rounded-full bg-[#AEDD1F] text-[#06251B] text-sm font-semibold hover:bg-[#9CCB1B] disabled:opacity-50 transition"
            >
              {webPush.busy ? 'Enabling…' : 'Enable notifications'}
            </button>
            {webPush.error && <p className="mt-2 text-xs text-red-600">{webPush.error}</p>}
          </div>
        )}
        {webPush.subscribed && (
          <p className="mt-5 pt-4 border-t border-gray-100 text-sm text-[#0A3B2C]">
            ✓ Notifications enabled
          </p>
        )}

        <button
          type="button"
          onClick={onResetAdded}
          className="mt-4 text-xs text-gray-500 underline hover:text-gray-700"
        >
          Didn't add it? Try again
        </button>
      </div>
    );
  }

  const googleButton = (
    <BadgeButton
      key="google"
      onClick={onGoogle}
      disabled={isDownloading || !googleReady}
      busy={isDownloading || !googleReady}
      img="/images/googleWalletLogo.svg"
      alt="Add to Google Wallet"
    />
  );
  const appleButton = (
    <BadgeButton
      key="apple"
      onClick={onApple}
      disabled={isDownloading}
      busy={isDownloading}
      img="/images/appleWalletLogo.svg"
      alt="Add to Apple Wallet"
    />
  );

  // Official Samsung badge from their CDN, self-hosted (never hand-drawn —
  // same rule as the Google badge). Rendered only when the backend says the
  // merchant+template Samsung gates are on.
  const samsungButton = (
    <BadgeButton
      key="samsung"
      onClick={onSamsung}
      disabled={isDownloading}
      busy={isDownloading}
      img="/images/samsungWalletBadge.png"
      alt="Add to Samsung Wallet"
    />
  );

  // Apple on iOS; on Android, Google — plus Samsung alongside when enabled
  // (2026-08-26 product decision: BOTH badges on Android, user's choice).
  // Cross-wallet users still have the "Add to another wallet" copy-link
  // fallback below.
  const buttons = isIOS
    ? [appleButton]
    : samsungAvailable ? [googleButton, samsungButton] : [googleButton];

  const previewButton = (
    <button
      type="button"
      onClick={onPreview}
      className="w-full h-11 rounded-full border border-[#E4EAE2] bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 transition flex items-center justify-center gap-2"
    >
      <svg className="w-4.5 h-4.5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" />
        <circle cx="12" cy="12" r="2.7" />
      </svg>
      Preview pass
    </button>
  );

  const secondaryBits = (
    <>
      <div className="text-center">
        <button
          type="button"
          onClick={() => setOtherOpen((v) => !v)}
          aria-expanded={otherOpen}
          className="inline-flex items-center gap-1 text-sm font-medium text-[#0A3B2C] hover:text-[#06251B] transition"
        >
          Add to another wallet
          <svg
            className={`w-4 h-4 transition-transform ${otherOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <AnimatePresence initial={false}>
          {otherOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-3 bg-white rounded-xl border border-[#E4EAE2] p-4 text-left space-y-3">
                <button
                  type="button"
                  onClick={copyLink}
                  className="w-full flex items-center gap-2.5 text-sm font-medium text-gray-800 hover:text-gray-900"
                >
                  <svg className="w-4.5 h-4.5 text-gray-500" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="9" y="9" width="12" height="12" rx="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                  {linkCopied ? 'Link copied ✓' : 'Copy this pass link'}
                </button>
                <p className="text-xs text-gray-500">
                  Open the link on any phone to add the pass there.{samsungAvailable ? '' : ' Samsung Wallet support is coming soon.'}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <p className="flex items-center justify-center gap-1.5 text-xs text-gray-500 pt-1">
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 118 0v4" />
        </svg>
        Safe, secure and private. Only you can see this pass.
      </p>
    </>
  );

  // Mobile below-the-card slice: Preview + add-to-another + trust line.
  if (variant === 'preview') {
    return (
      <div className="space-y-3">
        {previewButton}
        {secondaryBits}
      </div>
    );
  }

  // 'main' (mobile, above the card) = badge buttons only;
  // 'full' (desktop) = the whole stack.
  return (
    <div className="space-y-3">
      {buttons}
      {variant === 'full' && previewButton}
      {variant === 'full' && secondaryBits}
    </div>
  );
};

export default WalletCtaPanel;
